import * as React from 'react';
import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

export interface DataGridColumn<T> {
  id: string;
  header: React.ReactNode;
   
  cell(item: T): React.ReactNode;
  mobileLabel?: React.ReactNode;
  className?: string;
}

interface DataGridTableProps<T> {
  rows: T[];
  columns: DataGridColumn<T>[];
   
  getRowKey(item: T): React.Key;
  isLoading?: boolean;
  emptyMessage?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  className?: string;
}

export function DataGridTable<T>({
  rows,
  columns,
  getRowKey,
  isLoading = false,
  emptyMessage = 'No records found',
  onLoadMore,
  hasMore = false,
  className,
}: DataGridTableProps<T>) {
  const showEmpty = !isLoading && rows.length === 0;
  const showLoadMore = Boolean(onLoadMore) && hasMore;

  return (
    <div className={cn('@container/data-grid space-y-3', className)}>
      <div className="overflow-hidden rounded-xl border border-border/70">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.id} className={column.className}>
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <TableRow key={`loading-${index}`}>
                  <TableCell colSpan={columns.length}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.map((row) => (
              <TableRow key={getRowKey(row)}>
                {columns.map((column) => (
                  <TableCell key={`${String(getRowKey(row))}-${column.id}`} className={column.className}>
                    {column.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {showEmpty ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">{emptyMessage}</CardContent>
        </Card>
      ) : null}

      {showLoadMore ? (
        <div className="flex justify-center">
          <Button variant="outline" onClick={onLoadMore} disabled={isLoading}>
            {isLoading ? 'Loading…' : 'Load More'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
